import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useMyProducers } from '@/hooks/useMyProducers';

/**
 * Determines the producer entry state for the current user:
 * - "active_host": user has at least one producer/farm
 * - "in_progress": user has submitted interest or has a pending invitation
 * - "not_host": none of the above
 */
export type ProducerEntryState = 'not_host' | 'in_progress' | 'active_host';

export interface ProducerEntryInfo {
  state: ProducerEntryState;
  /** True while any underlying query is loading */
  isLoading: boolean;
  /** If in_progress, why? */
  reason: 'invitation' | 'interest' | null;
}

export function useProducerEntryState(): ProducerEntryInfo {
  const { user, isLoading: authLoading } = useAuth();
  const { hasProducers, isLoading: producersLoading } = useMyProducers();

  // Check for pending interest (matched by user_id)
  const { data: interestData, isLoading: interestLoading } = useQuery({
    queryKey: ['producer-interest-check', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('producer_interest')
        .select('id, status, created_at')
        .eq('user_id', user.id)
        .in('status', ['new', 'pending', 'submitted', 'reviewed'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (import.meta.env.DEV) {
        console.log('[useProducerEntryState] interest query', {
          userId: user.id,
          result: data,
          error,
        });
      }

      return data;
    },
    enabled: !!user?.id && !authLoading && !hasProducers,
    staleTime: 1000 * 60 * 5,
  });

  // Check for pending invitations
  const { data: invitationData, isLoading: invitationLoading } = useQuery({
    queryKey: ['producer-invitation-check', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data } = await supabase
        .from('producer_invitations')
        .select('id, status')
        .ilike('email', user.email)
        .eq('status', 'pending')
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.email && !authLoading && !hasProducers,
    staleTime: 1000 * 60 * 5,
  });

  const isLoading = authLoading || producersLoading || interestLoading || invitationLoading;

  if (isLoading) {
    return { state: 'not_host', isLoading: true, reason: null };
  }

  if (hasProducers) {
    return { state: 'active_host', isLoading: false, reason: null };
  }

  if (invitationData) {
    return { state: 'in_progress', isLoading: false, reason: 'invitation' };
  }

  if (interestData) {
    return { state: 'in_progress', isLoading: false, reason: 'interest' };
  }

  return { state: 'not_host', isLoading: false, reason: null };
}
